/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import SimCardInterface from '../../../interfaces/platform/SimCardInterface';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import { ProductActions } from '../../../modules/simcard/ProductReducer';
import { ApiCollectionInterface } from '../../../interfaces/collections/ApiCollectionInterface';
import { SystemLanguageInterface } from '../../../modules/language/interfaces/SystemLanguageInterface';
import { PlatformResellerAvailableLanguagesInterface } from '../../../modules/language/interfaces/PlatformResellerAvailableLanguagesInterface';
import i18next from 'i18next';

type ProductChangeLanguageChooseType = {
    product: SimCardInterface;
    open: boolean;
    close: () => void;
    reload: (languageId?: number | null) => void;
    isCreate: boolean;
};

export const ProductChangeLanguageChoose: FunctionComponent<ProductChangeLanguageChooseType> = ({
    isCreate,
    product,
    open,
    close,
    reload,
}): ReactElement => {
    const dispatch = useDispatch();
    const [systemLanguages, setSystemLanguages] = useState<ApiCollectionInterface<SystemLanguageInterface> | null>(null);
    const [platformResellerToSystemLanguages, setPlatformResellerToSystemLanguages] = useState<ApiCollectionInterface<
        PlatformResellerAvailableLanguagesInterface
    > | null>(null);
    const [languages, setLanguages] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [languageId, setLanguageId] = useState(-1);
    const [simCardId, setSimCardId] = useState(-1);

    useEffect(() => {
        if (product.id !== simCardId) {
            setSystemLanguages(null);
            setPlatformResellerToSystemLanguages(null);
            setLanguages(null);
            setLanguageId(-1);
            setSimCardId(product.id);
        }
    }, [product, simCardId]);

    useEffect(() => {
        if (!open) {
            setSystemLanguages(null);
            setPlatformResellerToSystemLanguages(null);
            setLanguages(null);
            setLanguageId(-1);
        }
    }, [open, product]);

    useEffect(() => {
        if (open && systemLanguages === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/system_languages')),
                'no-dispatch',
                [],
                [],
                (data: ApiCollectionInterface<SystemLanguageInterface>): void => {
                    setSystemLanguages(data);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [open, systemLanguages, dispatch]);

    useEffect(() => {
        if (platformResellerToSystemLanguages === null && open) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_reseller_to_system_languages')),
                'no-dispatch',
                [{ value: product.client.platformResellerId, name: 'platformResellerId' }],
                [],
                (data: ApiCollectionInterface<PlatformResellerAvailableLanguagesInterface>): void => {
                    setPlatformResellerToSystemLanguages(data);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [dispatch, platformResellerToSystemLanguages, product, open]);

    useEffect(() => {
        if (languages === null && systemLanguages && platformResellerToSystemLanguages) {
            const list: Array<HeaderCheckBoxSelectListElement> = [];
            for (let i = 0; i < platformResellerToSystemLanguages['hydra:member'].length; i++) {
                for (let j = 0; j < systemLanguages['hydra:member'].length; j++) {
                    if (platformResellerToSystemLanguages['hydra:member'][i].systemLanguageId === systemLanguages['hydra:member'][j].id) {
                        list.push({
                            name: i18next.t('general:language.' + systemLanguages['hydra:member'][j].language),
                            value: systemLanguages['hydra:member'][j].id ?? -1,
                        });
                    }
                }
            }
            setLanguages(list);
            setLanguageId(product.languageId);
        }
    }, [languages, systemLanguages, platformResellerToSystemLanguages, product]);

    const save = (): void => {
        if (languageId === null) {
            return;
        }
        if (isCreate) {
            reload(languageId);
            close();
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        const params = {
            activationType: 11,
            id: product.id,
            params: JSON.stringify({
                languageId: languageId,
            }),
        };
        itemLoader.putWithCallback(
            FormGeneralHelper.getUrl('/operation_on_sim_cards/' + product.id),
            'operation_on_sim_cards',
            params,
            (response: any) => {
                dispatch({
                    type: ProductActions.PRODUCT_REFRESH_VIEW,
                    uri: product['@id'],
                });
                close();
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = parseInt(event.target.value);
        setLanguageId(selected);
    };

    return (
        <Dialog open={open} onClose={close} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                <I18n trans="simcard:language.choose_language" />
            </DialogTitle>
            <DialogContent>
                <SimpleSelect
                    label="simcard:language.choose_language_button"
                    fieldName="useType"
                    required={true}
                    elements={languages ?? []}
                    dispatchName={'profile_id'}
                    selectedValue={languageId}
                    callback={onChangeSelectEvent}
                />
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={close} callbackParam={{}} label="general:common.close" size="sm" />
                {languageId !== product.languageId && <CustomTextButtom callback={save} callbackParam={{}} label="general:common.save" size="sm" />}
            </DialogActions>
        </Dialog>
    );
};
